import { LocalDate } from "@js-joda/core";
import currency from "currency.js";
import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import { dyn, dynVariadic } from "trader.ts/ui";
import { mapPartial } from "trader.ts/utils";
import { match } from "ts-pattern";
import { expectedpaymentid, iban, studentid } from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { renderCancelButton } from "../../client/savebutton";
import { invoiceFull, invoiceSubject } from "../../domain/finance/types";
import { capitalize } from "../../lib/capitalize";
import { partition } from "../../lib/partition";
import { ddMMM, renderCurrency, wrapInParentheses } from "../../render";
import * as routes from "../../routes";
import { describePeriod } from "../../translations_specific";
import { getInvoiceDirectDebitInfoStatus } from "../student_direct_debit_mandates/getenrollmentdirectdebitstatus";
import {
  renderEnrollmentCard,
  renderShowEnrollmentsOverSection,
  studentSectionInitStudentidSelection,
  studentSectionRenderStudentSelection,
} from "../student_start/client";

export function render(opts: {
  electronicPaymentsEnabled: boolean;
  students: {
    id: studentid;
    firstname: string;
    lastname: string;
    iban: iban | null;
    name_on_iban: string;
    direct_debit_allowed: boolean;
  }[];
  invoices: {
    invoice: invoiceFull;
    subject: invoiceSubject;
  }[];
}): HTMLElement | HTMLElement[] {
  const students = opts.students.uniqBy((stu) => stu.id);
  const studentS = studentSectionInitStudentidSelection(students);

  const needsDirectDebitInfo = opts.invoices.some(
    (inv) =>
      getInvoiceDirectDebitInfoStatus(
        inv.invoice,
        opts.students.find((stu) => stu.id === inv.subject.student.id)!,
      ) === "needs_info",
  );

  const selectingPaymentsS = new Source(false);
  const selectedPaymentsS = new Source(
    [] as { id: expectedpaymentid; amount_left: currency }[],
  );
  const today = LocalDate.now();

  return opts.invoices.length === 0 ? (
    <main className={style.full_width_center_800} style="max-width: unset;">
      <div>
        <b>{"We vonden geen facturen"}</b>
      </div>
    </main>
  ) : (
    [
      needsDirectDebitInfo ? renderNeedsDirectDebitInfoLink() : <span></span>,

      studentSectionRenderStudentSelection(students, studentS),

      <main className={style.full_width_center_800} style="max-width: unset;">
        <article style="text-align: right">
          {dynVariadic(
            [selectingPaymentsS, selectedPaymentsS, studentS],
            ([selecting, selectedPayments, student]) => {
              if (!opts.electronicPaymentsEnabled) {
                return <span></span>;
              }

              if (!selecting) {
                // Selecting relevant expected payments immediately
                const shownInvoices = opts.invoices.filter(
                  (e) =>
                    student.tag === "all" ||
                    student.id === e.subject.student.id,
                );
                const epsNotPaid = mapPartial(shownInvoices, (enr) => {
                  return enr.invoice && enr.invoice.amount_left.value > 0
                    ? (enr.invoice.expectedpayments || []).filter(
                        (ep) => ep.amount_left.value > 0,
                      )
                    : [];
                }).flat();
                if (epsNotPaid.length === 0) {
                  return (
                    <button className={style.button + style.button_em_down}>
                      {"Alles betaald"}
                    </button>
                  );
                }
                return (
                  <button
                    className={style.button + style.button_accent}
                    onclick={() => {
                      // Starting selection
                      selectingPaymentsS.set(true);

                      selectedPaymentsS.set(epsNotPaid);
                    }}
                  >
                    {"Start betaling"}
                  </button>
                );
              } else {
                const cancelbutton = renderCancelButton({
                  onclick: () => selectingPaymentsS.set(false),
                  style: "background-color: white; color: black; ",
                });

                const redirectButton =
                  renderExpectedPaymentsRedirectButton(selectedPayments);

                return [cancelbutton, redirectButton];
              }
            },
          )}
        </article>

        {dyn(studentS, (student) => {
          const invoicesForStudent = opts.invoices.filter(
            (e) => student.tag === "all" || e.subject.student.id === student.id,
          );

          function isOlder(a: invoiceSubject) {
            return a.period.lastday.isBefore(today.minusMonths(1));
          }

          const [invsRecent, invsOlder] = partition(
            invoicesForStudent,
            (e) => !isOlder(e.subject),
          );

          const showOlderS = new Source(
            invsRecent.length === 0 ||
              invsOlder.some((e) => e.invoice.amount_left.intValue > 0),
          );

          return dyn(selectingPaymentsS, (selectingPayments) => (
            <div>
              {
                // Rendering recent enrollments
                invsRecent.map((inv) =>
                  renderInvoice(
                    selectedPaymentsS,
                    selectingPayments,
                    student,
                    inv,
                  ),
                )
              }

              {renderShowEnrollmentsOverSection(invsOlder.length, showOlderS)}

              {dynVariadic([showOlderS], ([show]) => {
                // Rendering older enrollments
                return show
                  ? invsOlder.map((inv) =>
                      renderInvoice(
                        selectedPaymentsS,
                        selectingPayments,
                        student,
                        inv,
                      ),
                    )
                  : [];
              })}
            </div>
          ));
        })}
      </main>,
    ]
  );
}

function renderInvoice(
  selectedPaymentsS: Source<{ id: expectedpaymentid; amount_left: currency }[]>,
  selectingPayments: boolean,
  student:
    | { tag: "all" }
    | {
        tag: "student";
        id: studentid;
        firstname: string;
        lastname: string;
      },
  e: { subject: invoiceSubject; invoice: invoiceFull },
): HTMLElement {
  const invoiceRendered = e.invoice ? (
    <div className={style.text_with_icon}>
      <i className={FA.icon_euro_sign_solid_1}></i>
      <div>
        {renderCurrency(e.invoice.amount_total)}
        <br></br>
        <a
          href={routes.downloadInvoicePdfSpec.route.link({
            invoiceid: e.invoice.id,
          })}
          download={"invoice-" + e.invoice.externalidstring + ".pdf"}
        >
          {"Download factuur"}
        </a>
      </div>
    </div>
  ) : (
    <div>{"Geen factuur"}</div>
  );

  const paymentRows: HTMLElement[] = renderExpectedPaymentRows(
    { ...e, periodfeeInvoice: null },
    selectingPayments,
    selectedPaymentsS,
  );

  const invoiceHtml = (
    <div>
      <article
        className={style.cancel_card_padding}
        style={`margin-top: 12px;
                margin-bottom: 12px;
                background-color: var(--grey-150);
                padding-top: 12px;
                padding-bottom: 20px; `}
      >
        <label for="">
          {"Factuur" +
            " " +
            wrapInParentheses("#" + e.invoice.externalidstring)}
        </label>
        {invoiceRendered}
      </article>

      <div
        className={style.cancel_card_padding}
        style="background-color: white; padding-bottom: 16px; border-radius: 0px 0px 8px 8px;"
      >
        <label for="">{"Verwachte betalingen"}</label>
        {paymentRows}
      </div>
    </div>
  );

  const showStudentName = student.tag === "all";

  const rendered = match(e.subject)
    .with({ tag: "enrollment" }, (e) =>
      renderEnrollmentCard(
        {
          showStudentName,
          style:
            "padding-bottom: 0px; " +
            (selectingPayments ? "background-color: var(--grey-150);" : ""),
          extraHtml: invoiceHtml,
        },
        {
          student: e.student,
          status: e.status,
          lessongroup: e.lg,
          instrument: e.instrument,
          teacher: e.teacher,
          room_description: e.room_description, // not relevant here
        },
      ),
    )
    .with({ tag: "periodfee" }, (pf) => (
      <div>
        <h3>{"Jaargeld"}</h3>
        <h2>{describePeriod(pf.period.firstday, pf.period.lastday)}</h2>

        <article className={style.equal_columns_non_responsive}>
          {showStudentName ? (
            <div className={style.text_with_icon}>
              <i className={FA.icon_user_solid}></i>
              <div style={"text-decoration: none;"}>
                {capitalize(pf.student.firstname.toLowerCase())}
              </div>
            </div>
          ) : (
            []
          )}
        </article>
      </div>
    ))
    .with({ tag: "student_charge" }, (inv) => (
      <div>
        <h3>{"Factuur"}</h3>
        <h2>{inv.student_charge.description}</h2>

        <article className={style.equal_columns_non_responsive}>
          {showStudentName ? (
            <div className={style.text_with_icon}>
              <i className={FA.icon_user_solid}></i>
              <div style={"text-decoration: none;"}>
                {capitalize(inv.student.firstname.toLowerCase())}
              </div>
            </div>
          ) : (
            []
          )}
        </article>
      </div>
    ))
    .with({ tag: "enrollmentrequest" }, () => <span></span>)
    .exhaustive();

  return rendered;
}

export function renderExpectedPaymentsRedirectButton(
  selectedPayments: { id: expectedpaymentid; amount_left: currency }[],
) {
  const disabled = selectedPayments.length === 0 ? true : undefined;

  return (
    <a
      style="margin-left: 16px; margin-top: 16px;"
      href={
        disabled
          ? undefined
          : routes.payExpectedPaymentsRoute.link({
              expectedpayments: {
                expectedpaymentids: selectedPayments.map((sl) => sl.id),
              },
            })
      }
    >
      <button
        className={style.button + style.button_accent}
        disabled={disabled || undefined}
      >
        {selectedPayments.length === 0
          ? "Selecteer betalingen"
          : "Betaal %amount"
              .replace("%n", selectedPayments.length.toString())
              .replace(
                "%amount",
                renderCurrency(
                  selectedPayments.reduce(
                    (acc, sel) => acc.add(sel.amount_left),
                    currency(0),
                  ),
                ),
              )}
      </button>
    </a>
  );
}

export function renderExpectedPaymentRows(
  e: {
    invoice: invoiceFull | null;
    periodfeeInvoice: invoiceFull | null;
  },
  selectingPayments: boolean,
  selectedPaymentsS: Source<{ id: expectedpaymentid; amount_left: currency }[]>,
) {
  const allExpectedPayments = (e.invoice?.expectedpayments || []).concat(
    e.periodfeeInvoice?.expectedpayments || [],
  );
  const paymentRows: HTMLElement[] = allExpectedPayments.flatMap((exppaym) => {
    const fullyPaid: boolean = exppaym.amount_left.value === 0;
    const partiallyPaidString =
      exppaym.amount_left.value > 0 && exppaym.amount_paid.value > 0
        ? ", %amount betaald".replace(
            "%amount",
            renderCurrency(exppaym.amount_paid),
          )
        : "";
    return dynVariadic([selectedPaymentsS], ([selectedPayments]) => {
      const selected = selectedPayments.some((sel) => sel.id === exppaym.id);
      return (
        <label
          style={
            "display: flex; font-size: unset; text-transform: none;" +
            (selectingPayments ? "font-weight: 600;" : "font-weight: 100;")
          }
        >
          <div className={style.text_with_icon} style="flex: 1">
            <i className={FA.icon_euro_sign_solid_1}></i>
            {renderCurrency(exppaym.amount)} (
            {"voor %date".replace("%date", ddMMM(exppaym.date)) +
              partiallyPaidString}
            )
          </div>

          <div style="flex: 0 1 auto;">
            {fullyPaid ? (
              <span style="text-style: italic">{"Betaald"}</span>
            ) : selectingPayments ? (
              <div>
                <input
                  type="checkbox"
                  checked={selected}
                  onclick={() => {
                    const curr = selectedPaymentsS.get();
                    selectedPaymentsS.set(
                      selected
                        ? curr.filter((c) => c.id !== exppaym.id)
                        : curr.concat({
                            id: exppaym.id,
                            amount_left: exppaym.amount_left,
                          }),
                    );
                  }}
                />
                {renderCurrency(exppaym.amount_left)}
              </div>
            ) : (
              <span></span>
            )}
          </div>
        </label>
      );
    });
  });
  return paymentRows;
}

function renderNeedsDirectDebitInfoLink() {
  return (
    <div
      className={style.full_width_center_800}
      style="background-color: white;"
    >
      <a href={routes.student_direct_debit_mandatesRoute.link()}>
        <div className={style.card + style.card_large + style.card_accent}>
          <b>
            {"We konden je automatische incasso nog niet opstarten. Click hier om dit in orde te brengen."}
          </b>
        </div>
      </a>
    </div>
  );
}
